'use client';
import { useMemo, useRef, useState } from 'react';
import { useIsAuthed } from '../../hooks/useIsAuthed';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { ScreenContent, ScreenFixedTop, ScreenFixedBottom } from '../../screen';
import { Button, ButtonGroup, EmptyState } from '../../components/ui';
import { FaQuestionCircle } from '../../components/icons';
import translations from './AchievementsScreen.translations';
import { useCatalog } from './hooks/useCatalog';
import { useMyAchievements, type MergedAchievement } from './hooks/useMyAchievements';
import { AchievementClaimBanner } from './components/AchievementClaimBanner';
import { AchievementRulesSheet } from './components/AchievementRulesSheet';
import { AchievementGrid } from './components/AchievementGrid';
import { AchievementGridSkeleton } from './components/AchievementGridSkeleton';
import { AchievementTabs, type Category } from './components/AchievementTabs';
import { filterByTab } from './filterByTab';

export function AchievementsScreen() {
  const [cat, setCat] = useState<Category>('all');
  const [rulesOpen, setRulesOpen] = useState(false);
  const { t } = useLocalTranslation(translations);
  const scrollRef = useRef<HTMLDivElement>(null);
  const isAuthed = useIsAuthed();
  const { data: mine } = useMyAchievements();
  const { data: catalog } = useCatalog();

  const items: MergedAchievement[] | null = useMemo(() => {
    if (isAuthed) return mine?.items ?? null;
    if (!catalog) return null;
    return catalog.items.map((c) => ({ ...c, unlockedAt: null, progress: null, rewardClaimed: false }));
  }, [isAuthed, mine, catalog]);

  const filtered = items ? filterByTab(items, cat) : [];

  return (
    <>
      <ScreenFixedTop title={t('achievementsTitle')}>
        <AchievementTabs active={cat} onChange={setCat} />

        <div className="flex items-center justify-center gap-4 px-4 pb-2 text-xs text-label-secondary">
          <span className="flex items-center gap-1.5">
            <span aria-hidden className="h-0 w-0 border-l-[8px] border-t-[8px] border-l-transparent border-t-accent-green" />
            {t('legendCredited')}
          </span>
          <span className="flex items-center gap-1.5">
            <span aria-hidden className="h-0 w-0 border-l-[8px] border-t-[8px] border-l-transparent border-t-price" />
            {t('legendUnclaimed')}
          </span>
        </div>
      </ScreenFixedTop>
      <ScreenContent scrollRef={scrollRef} scrollKey="achievements">
        <AchievementClaimBanner />
        {!items ? (
          <AchievementGridSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState message={t('locked')} />
        ) : (
          <AchievementGrid items={filtered} className="pb-0 lg:pb-4" />
        )}
      </ScreenContent>
      <ScreenFixedBottom>
        <ButtonGroup>
          <Button size="l" stretched data-sound="menu_select" before={<FaQuestionCircle />} onClick={() => setRulesOpen(true)}>
            {t('whatIsThis')}
          </Button>
        </ButtonGroup>
      </ScreenFixedBottom>
      <AchievementRulesSheet isOpen={rulesOpen} onClose={() => setRulesOpen(false)} />
    </>
  );
}
